import * as React from 'react';
import Button from '@material-ui/core/Button';
import TextField from '@material-ui/core/TextField';
import InputAdornment from '@material-ui/core/InputAdornment';
import VisibilityIcon from '@material-ui/icons/Visibility';
import VisibilityIconOff from '@material-ui/icons/VisibilityOff';
import MenuItem from '@material-ui/core/MenuItem';
import Switch from '@material-ui/core/Switch';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import CloudUploadOutlined from '@material-ui/icons/CloudUploadOutlined';
import SaveOutlined from '@material-ui/icons/SaveOutlined';
import axios from 'axios';
import { BASE_URL, SCORE_URL } from '../BASE_CONSTS';
import Cookies from '../provider/helpers/Cookies';
import playerPlaceholder from '../assets/player-placeholder.svg';
import { getPlayerPhotoUrl } from './playerViewModel';

const choices = [
  { id: 'Вратарь', name: 'Вратарь' },
  { id: 'Защитник(ЦЗ)', name: 'Защитник(ЦЗ)' },
  { id: 'Крайний защитник', name: 'Крайний защитник (Лз,Пз)' },
  { id: 'Центральный ПЗ', name: 'Центральный Полузащитник' },
  { id: 'Атакующий ПЗ', name: 'Атакующий Полузащитник' },
  { id: 'Опорный ПЗ', name: 'Опорный Полузащитник' },
  { id: 'Фланговый ПЗ', name: 'Фланговый Полузащитник (ПП,ЛП)' },
  { id: 'Нападающий', name: 'Нападающий' },
  { id: 'Ф. Нападающий', name: 'Фланговый Нападающий' },
];

const choicesLeg = [
  { id: 'Правая', name: 'Правая' },
  { id: 'Левая', name: 'Левая' },
];

const choicesLessons = [
  '1 занятие',
  '8 занятий',
  '1 месяц',
  '2 месяца',
  '3 месяца',
  '6 месяцев',
  ' - ',
];

const authHeaders = () => ({ Authorization: `Bearer ${Cookies.getCookie('token')}` });
const dateValue = (value) => (value ? String(value).slice(0, 10) : '');

const Notice = ({ type, children }) => children ? <div className={`fs-admin-alert fs-admin-alert--${type}`}>{children}</div> : null;

export const ChangeMainInfo = ({ id }) => {
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [footballPlayer, setFootballPlayer] = React.useState(null);
  const [showPassword, setShowPassword] = React.useState(false);
  const [file, setFile] = React.useState(null);
  const [preview, setPreview] = React.useState('');
  const [playerPoints, setPlayerPoints] = React.useState(0);
  const [notice, setNotice] = React.useState({ type: '', text: '' });
  const pointsUrl = `${String(SCORE_URL).replace(/\/+$/, '')}/points/${id}/`;

  React.useEffect(() => {
    let cancelled = false;
    Promise.allSettled([
      fetch(`${BASE_URL}/footballers/${id}`, { headers: authHeaders() }).then((response) => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return response.json();
      }),
      fetch(pointsUrl).then((response) => response.ok ? response.json() : null),
    ]).then(([playerResult, pointsResult]) => {
      if (cancelled) return;
      if (playerResult.status === 'fulfilled') setFootballPlayer(playerResult.value);
      else setNotice({ type: 'error', text: `Не удалось загрузить игрока: ${playerResult.reason.message}` });
      if (pointsResult.status === 'fulfilled' && pointsResult.value?.body) {
        setPlayerPoints(Number(pointsResult.value.body.count_of_point) || 0);
      }
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, [id, pointsUrl]);

  React.useEffect(() => () => {
    if (preview && preview.startsWith('blob:')) URL.revokeObjectURL(preview);
  }, [preview]);

  const change = (field) => (event) => {
    const value = event.target.value;
    setFootballPlayer((player) => ({ ...player, [field]: value }));
    setNotice({ type: '', text: '' });
  };

  const choosePhoto = (event) => {
    const selected = event.target.files && event.target.files[0];
    if (!selected) return;
    if (preview && preview.startsWith('blob:')) URL.revokeObjectURL(preview);
    setFile(selected);
    setPreview(URL.createObjectURL(selected));
    setNotice({ type: '', text: '' });
  };

  const uploadPhoto = async () => {
    if (!file) return null;
    const body = new FormData();
    body.append('files', file);
    const response = await fetch(`${String(BASE_URL).replace(/\/+$/, '')}/upload/`, {
      method: 'POST',
      body,
      headers: authHeaders(),
    });
    if (!response.ok) throw new Error(`Не удалось загрузить фотографию (HTTP ${response.status})`);
    const data = await response.json();
    if (!data || !data[0]) throw new Error('Strapi не вернул загруженную фотографию');
    return data[0];
  };

  const saveData = async () => {
    if (!footballPlayer || saving) return;
    setSaving(true);
    setNotice({ type: '', text: '' });
    try {
      const uploadedAvatar = await uploadPhoto();
      const payload = { ...footballPlayer };
      if (uploadedAvatar) payload.avatar = uploadedAvatar;

      if (Number.isFinite(Number(playerPoints))) {
        await axios.get(`${pointsUrl}${Number(playerPoints)}/`);
      }

      const response = await axios.put(`${BASE_URL}/footballers/${id}`, payload, {
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
      });
      setFootballPlayer(response.data || payload);
      setFile(null);
      setPreview('');
      setNotice({ type: 'success', text: 'Изменения игрока сохранены.' });
    } catch (error) {
      setNotice({
        type: 'error',
        text: `Не удалось сохранить игрока: ${error.response?.data?.message || error.message}`,
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="fs-admin-empty">Загрузка игрока…</div>;
  if (!footballPlayer) return <Notice type="error">Игрок не загружен.</Notice>;

  const currentPhoto = preview || getPlayerPhotoUrl(footballPlayer) || playerPlaceholder;

  return (
    <div className="fs-player-edit-form">
      <Notice type={notice.type}>{notice.text}</Notice>

      <section className="fs-form-section fs-form-section--photo">
        <div className="fs-form-section__heading">
          <div><span>ПРОФИЛЬ</span><h2>Фотография игрока</h2></div>
          <p>Фотография используется в карточке игрока и грамотах.</p>
        </div>
        <div className="fs-photo-editor">
          <img
            src={currentPhoto}
            alt="Фотография игрока"
            onError={(event) => { event.currentTarget.src = playerPlaceholder; }}
          />
          <div className="fs-photo-editor__actions">
            <strong>{file ? file.name : 'Текущая фотография'}</strong>
            <span>JPG или PNG. Выбранная фотография загрузится при сохранении.</span>
            <label className="fs-photo-select-button">
              <CloudUploadOutlined />
              {file ? 'Выбрать другую' : 'Выбрать фотографию'}
              <input type="file" accept="image/*" onChange={choosePhoto} />
            </label>
            {file ? <Button onClick={() => { setFile(null); setPreview(''); }}>Отменить выбор</Button> : null}
          </div>
        </div>
      </section>

      <section className="fs-form-section">
        <div className="fs-form-section__heading">
          <div><span>ОСНОВНЫЕ ДАННЫЕ</span><h2>Игрок</h2></div>
          <p>Контактные данные и информация, отображаемая в личном кабинете.</p>
        </div>
        <div className="fs-form-grid">
          <TextField variant="outlined" label="Телефон / логин" value={footballPlayer.f_email || ''} onChange={change('f_email')} />
          <TextField
            variant="outlined"
            label="Пароль"
            type={showPassword ? 'text' : 'password'}
            value={footballPlayer.f_password || ''}
            onChange={change('f_password')}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Button className="fs-icon-button" onClick={() => setShowPassword((value) => !value)}>
                    {showPassword ? <VisibilityIconOff /> : <VisibilityIcon />}
                  </Button>
                </InputAdornment>
              ),
            }}
          />
          <TextField variant="outlined" label="Имя" value={footballPlayer.firstname || ''} onChange={change('firstname')} />
          <TextField variant="outlined" label="Фамилия" value={footballPlayer.lastname || ''} onChange={change('lastname')} />
          <TextField variant="outlined" label="Команда" value={footballPlayer.team || ''} onChange={change('team')} />
          <TextField variant="outlined" label="Дата рождения" type="date" value={dateValue(footballPlayer.birthday)} onChange={change('birthday')} InputLabelProps={{ shrink: true }} />
          <TextField select variant="outlined" label="Позиция игрока" value={footballPlayer.playerPosition || ''} onChange={change('playerPosition')}>
            {choices.map((option) => <MenuItem key={option.id} value={option.name}>{option.name}</MenuItem>)}
          </TextField>
          <TextField select variant="outlined" label="Ведущая нога" value={footballPlayer.lead_leg || ''} onChange={change('lead_leg')}>
            {choicesLeg.map((option) => <MenuItem key={option.id} value={option.name}>{option.name}</MenuItem>)}
          </TextField>
          <TextField variant="outlined" label="Баллы" type="number" value={playerPoints} onChange={(event) => setPlayerPoints(Number(event.target.value))} />
          <div className="fs-switch-field">
            <FormControlLabel
              control={<Switch checked={Boolean(footballPlayer.Active)} onChange={(event) => setFootballPlayer((player) => ({ ...player, Active: event.target.checked }))} color="primary" />}
              label={footballPlayer.Active ? 'Активный игрок' : 'Игрок неактивен'}
            />
          </div>
        </div>
      </section>

      <div className="fs-form-actions">
        <Button className="fs-button-primary" variant="contained" onClick={saveData} disabled={saving} startIcon={<SaveOutlined />}>
          {saving ? 'Сохранение…' : 'Сохранить изменения'}
        </Button>
      </div>
    </div>
  );
};

export const ChangeSecondaryInfo = ({ id }) => {
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [player, setPlayer] = React.useState(null);
  const [notice, setNotice] = React.useState({ type: '', text: '' });

  React.useEffect(() => {
    fetch(`${BASE_URL}/footballers/${id}`, { headers: authHeaders() })
      .then((response) => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return response.json();
      })
      .then(setPlayer)
      .catch((error) => setNotice({ type: 'error', text: `Не удалось загрузить данные оплаты: ${error.message}` }))
      .finally(() => setLoading(false));
  }, [id]);

  const change = (field) => (event) => setPlayer((value) => ({ ...value, [field]: event.target.value }));

  const save = async () => {
    setSaving(true);
    setNotice({ type: '', text: '' });
    try {
      const response = await axios.put(`${BASE_URL}/footballers/${id}`, player, { headers: authHeaders() });
      setPlayer(response.data || player);
      setNotice({ type: 'success', text: 'Данные оплаты сохранены.' });
    } catch (error) {
      setNotice({ type: 'error', text: `Не удалось сохранить данные оплаты: ${error.response?.data?.message || error.message}` });
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="fs-admin-empty">Загрузка данных оплаты…</div>;
  if (!player) return <Notice type="error">Данные игрока недоступны.</Notice>;

  return (
    <div className="fs-player-edit-form">
      <Notice type={notice.type}>{notice.text}</Notice>
      <section className="fs-form-section">
        <div className="fs-form-section__heading">
          <div><span>ОПЛАТА</span><h2>Абонемент игрока</h2></div>
          <p>Дата последней оплаты и текущий вариант абонемента.</p>
        </div>
        <div className="fs-form-grid">
          <TextField variant="outlined" label="Дата последней оплаты" type="date" value={dateValue(player.date_of_last_pay)} onChange={change('date_of_last_pay')} InputLabelProps={{ shrink: true }} />
          <TextField select variant="outlined" label="Занятия / месяцы" value={player.number_of_lessons || ''} onChange={change('number_of_lessons')}>
            {choicesLessons.map((option) => <MenuItem key={option} value={option}>{option}</MenuItem>)}
          </TextField>
          <TextField variant="outlined" label="Вариант абонемента" value={player.variant_of_subscription || ''} onChange={change('variant_of_subscription')} />
        </div>
      </section>
      <div className="fs-form-actions">
        <Button className="fs-button-primary" variant="contained" disabled={saving} onClick={save} startIcon={<SaveOutlined />}>
          {saving ? 'Сохранение…' : 'Сохранить оплату'}
        </Button>
      </div>
    </div>
  );
};
